import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listWarehouses, getOeeRule, putOeeRule, WEEKDAYS,
  type ShiftWindow, type DayCode,
} from "../api/rest";
import { useSiteId } from "../hooks/useSiteId";
import { useSiteStore } from "../stores/siteStore";
import css from "./ShiftsAdminPage.module.css";

const DAY_MIN = 1440;

/** Absent `days` means every day (ADR 257), so an untouched shift shows all seven lit. */
function daysOf(s: ShiftWindow): readonly DayCode[] {
  return s.days ?? WEEKDAYS;
}

function runsOn(s: ShiftWindow, day: DayCode): boolean {
  return daysOf(s).includes(day);
}

/** "HH:MM" → minutes since midnight, or NaN if malformed. */
function minutesOf(hhmm: string): number {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return NaN;
  const h = +m[1], mi = +m[2];
  if (h > 23 || mi > 59) return NaN;
  return h * 60 + mi;
}

interface Seg { leftPct: number; widthPct: number; }

/** A shift with end ≤ start wraps past midnight → two segments on the 24 h track. */
function segmentsOf(shifts: ShiftWindow[]): Seg[] {
  const segs: Seg[] = [];
  const push = (from: number, to: number) =>
    segs.push({ leftPct: (from / DAY_MIN) * 100, widthPct: ((to - from) / DAY_MIN) * 100 });
  for (const s of shifts) {
    const a = minutesOf(s.start), b = minutesOf(s.end);
    if (Number.isNaN(a) || Number.isNaN(b) || a === b) continue;
    if (b > a) push(a, b);
    else { push(a, DAY_MIN); push(0, b); }
  }
  return segs;
}

function shiftIsBad(s: ShiftWindow): boolean {
  return Number.isNaN(minutesOf(s.start)) || Number.isNaN(minutesOf(s.end)) || s.start === s.end;
}

export function ShiftsAdminPage() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const qc = useQueryClient();
  const activeWarehouseId = useSiteStore((s) => s.activeWarehouseId);
  const timezone = useSiteStore((s) => s.availableSites.find((x) => x.id === siteId)?.timezone);

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses", siteId],
    queryFn: () => listWarehouses(siteId),
  });

  // Local warehouse selection — the user's pick wins while it exists; otherwise fall
  // back to the global active warehouse, else the first. Pure derivation (no effect):
  // an invalid/unset pick self-heals on the next render instead of via setState.
  const [pickedWarehouseId, setPickedWarehouseId] = useState("");
  const warehouseId =
    pickedWarehouseId && warehouses.some((w) => w.id === pickedWarehouseId)
      ? pickedWarehouseId
      : activeWarehouseId && warehouses.some((w) => w.id === activeWarehouseId)
        ? activeWarehouseId
        : warehouses[0]?.id ?? "";

  const { data: rule, isLoading } = useQuery({
    queryKey: ["oeeRule", siteId, warehouseId],
    queryFn: () => getOeeRule(siteId, warehouseId),
    enabled: !!warehouseId,
  });

  const [shifts, setShifts] = useState<ShiftWindow[]>([]);
  const [ideal, setIdeal] = useState("90");
  const [saved, setSaved] = useState(false);
  // Guarded adjust-during-render (react.dev pattern): re-seed the editor once per
  // fetched rule identity (warehouse switch or refetch), as the old effect did.
  const [seededFrom, setSeededFrom] = useState<unknown>(null);
  if (rule && rule !== seededFrom) {
    setSeededFrom(rule);
    setShifts(rule.shifts);
    setIdeal(String(rule.idealCycleS));
    setSaved(false);
  }

  const save = useMutation({
    mutationFn: () => putOeeRule(siteId, warehouseId, { idealCycleS: Number(ideal), shifts }),
    onSuccess: (r) => {
      setShifts(r.shifts);
      setIdeal(String(r.idealCycleS));
      setSaved(true);
      qc.invalidateQueries({ queryKey: ["oeeRule", siteId, warehouseId] });
    },
  });

  const errors: string[] = [];
  shifts.forEach((s, i) => {
    if (Number.isNaN(minutesOf(s.start)) || Number.isNaN(minutesOf(s.end)))
      errors.push(t("shiftsAdmin.err.time", { n: i + 1 }));
    else if (s.start === s.end) errors.push(t("shiftsAdmin.err.equal", { n: i + 1 }));
    // A shift that runs on no day is rejected by the API rather than stored, because the
    // reader would read the empty list back as "no restriction" (ADR 257).
    if (s.days && s.days.length === 0) errors.push(t("shiftsAdmin.err.noDays", { n: i + 1 }));
  });
  const idealNum = Number(ideal);
  if (!Number.isFinite(idealNum) || idealNum <= 0) errors.push(t("shiftsAdmin.err.ideal"));

  const dirty =
    !!rule && (JSON.stringify(shifts) !== JSON.stringify(rule.shifts) || idealNum !== rule.idealCycleS);
  const touch = () => setSaved(false);
  const updateShift = (i: number, field: keyof ShiftWindow, value: string) => {
    setShifts((prev) => prev.map((s, j) => (j === i ? { ...s, [field]: value } : s)));
    touch();
  };
  const addShift = () => { setShifts((prev) => [...prev, { start: "08:00", end: "17:00" }]); touch(); };
  const removeShift = (i: number) => { setShifts((prev) => prev.filter((_, j) => j !== i)); touch(); };

  /** All seven selected is the same statement as no restriction, so it stores as omitted —
   *  one canonical shape in the column instead of two that mean the same thing. */
  const toggleDay = (i: number, day: DayCode) => {
    setShifts((prev) => prev.map((s, j) => {
      if (j !== i) return s;
      const on = new Set(daysOf(s));
      if (on.has(day)) on.delete(day); else on.add(day);
      const next = WEEKDAYS.filter((d) => on.has(d));
      return { start: s.start, end: s.end, ...(next.length === WEEKDAYS.length ? {} : { days: next }) };
    }));
    touch();
  };

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.title}>{t("shiftsAdmin.title")}</div>
        <div className={css.subtitle}>{t("shiftsAdmin.subtitle")}</div>
      </div>

      <div className={css.selectorRow}>
        <div className={`${css.field} ${css.grow}`}>
          <label className={css.label} htmlFor="shifts-wh">{t("shiftsAdmin.warehouse")}</label>
          <select
            id="shifts-wh"
            className={css.select}
            value={warehouseId}
            onChange={(e) => setPickedWarehouseId(e.target.value)}
            disabled={warehouses.length === 0}
          >
            {warehouses.length === 0 && <option value="">{t("shiftsAdmin.noWarehouses")}</option>}
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </div>
      </div>

      {warehouses.length === 0 ? (
        <div className={css.card}>{t("shiftsAdmin.noWarehouses")}</div>
      ) : isLoading || !rule ? (
        <div className={css.card}>
          <div className={`${css.skeleton} ${css.skelBar}`} />
          <div className={`${css.skeleton} ${css.skelRow}`} />
          <div className={`${css.skeleton} ${css.skelRow}`} />
        </div>
      ) : (
        <div className={css.card}>
          <div className={css.cardTitle}>{t("shiftsAdmin.calendar")}</div>
          <div className={css.tzNote}>{t("shiftsAdmin.tz")} <b>{timezone ?? "UTC"}</b></div>

          {/* One track per weekday (ADR 257): on a single 24 h bar a Saturday-only shift
              painted exactly like an everyday one, which is the error the feature exists
              to make visible. */}
          <div className={css.coverage}>
            {WEEKDAYS.map((day) => {
              const daySegments = segmentsOf(shifts.filter((s) => runsOn(s, day)));
              return (
                <div key={day} className={css.weekRow}>
                  <span className={css.dayLabel}>{t(`shiftsAdmin.day.${day}`)}</span>
                  <div
                    className={css.track}
                    role="img"
                    aria-label={t("shiftsAdmin.coverageAria", { day: t(`shiftsAdmin.dayFull.${day}`) })}
                  >
                    {shifts.length === 0 ? (
                      <div className={css.trackFull} />
                    ) : (
                      daySegments.map((seg, i) => (
                        <div key={i} className={css.segment} style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%` }} />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
            <div className={css.weekRow}>
              <span className={css.dayLabel} aria-hidden />
              <div className={css.ticks}>
                {[0, 6, 12, 18, 24].map((h) => (
                  <span key={h} className={css.tick} style={{ left: `${(h / 24) * 100}%` }}>
                    {String(h).padStart(2, "0")}h
                  </span>
                ))}
              </div>
            </div>
            {shifts.length === 0 && <div className={css.coverageEmpty}>{t("shiftsAdmin.empty247")}</div>}
          </div>

          <div className={css.shiftList}>
            {shifts.map((s, i) => {
              const bad = shiftIsBad(s);
              const overnight = !bad && minutesOf(s.end) <= minutesOf(s.start);
              return (
                <div key={i} className={bad ? `${css.shiftRow} ${css.rowBad}` : css.shiftRow}>
                  <input
                    type="time" className={`${css.input} ${css.timeInput}`} value={s.start}
                    aria-label={t("shiftsAdmin.startAria", { n: i + 1 })}
                    onChange={(e) => updateShift(i, "start", e.target.value)}
                  />
                  <span className={css.dash}>—</span>
                  <input
                    type="time" className={`${css.input} ${css.timeInput}`} value={s.end}
                    aria-label={t("shiftsAdmin.endAria", { n: i + 1 })}
                    onChange={(e) => updateShift(i, "end", e.target.value)}
                  />
                  {overnight && <span className={css.overnight}>{t("shiftsAdmin.overnight")}</span>}
                  <div className={css.dayPicker} role="group" aria-label={t("shiftsAdmin.daysAria", { n: i + 1 })}>
                    {WEEKDAYS.map((day) => {
                      const on = runsOn(s, day);
                      return (
                        <button
                          key={day}
                          type="button"
                          className={on ? `${css.dayChip} ${css.dayChipOn}` : css.dayChip}
                          aria-pressed={on}
                          aria-label={t(`shiftsAdmin.dayFull.${day}`)}
                          onClick={() => toggleDay(i, day)}
                        >
                          {t(`shiftsAdmin.day.${day}`)}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button" className={css.iconBtn}
                    aria-label={t("shiftsAdmin.remove", { n: i + 1 })}
                    onClick={() => removeShift(i)}
                  >×</button>
                </div>
              );
            })}
          </div>

          <button type="button" className={css.addBtn} onClick={addShift}>+ {t("shiftsAdmin.addShift")}</button>

          <div className={css.selectorRow} style={{ marginTop: "var(--space-5)" }}>
            <div className={css.field}>
              <label className={css.label} htmlFor="shifts-ideal">{t("shiftsAdmin.idealCycle")}</label>
              <input
                id="shifts-ideal" type="number" min={1} className={`${css.input} ${css.idealInput}`}
                value={ideal} onChange={(e) => { setIdeal(e.target.value); touch(); }}
              />
            </div>
          </div>

          <div className={css.footer}>
            <button
              type="button" className={css.btn}
              disabled={errors.length > 0 || save.isPending || !dirty}
              onClick={() => save.mutate()}
            >
              {save.isPending ? t("common.saving") : t("common.save")}
            </button>
            {errors.length > 0 ? (
              <ul className={css.errors}>{errors.map((e, i) => <li key={i} className={css.errorText}>{e}</li>)}</ul>
            ) : save.isError ? (
              <span className={css.errorText}>{t("shiftsAdmin.saveError", { msg: (save.error as Error).message })}</span>
            ) : saved && !dirty ? (
              <span className={css.savedText}>✓ {t("shiftsAdmin.saved")}</span>
            ) : dirty ? (
              <span className={css.dirtyHint}>{t("shiftsAdmin.unsaved")}</span>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
